/**
 * WardrobeScene V2 — Salon Piękności jako SCENA-POKÓJ (PLAN-V2 §5.2).
 * AnimatedDogAvatar chodzi swobodnie; wieszaki = InteractiveObject (interact/);
 * drag na psa → setOutfit + happy; lustro zdejmuje sloty; polaroidy; debounce PUT.
 */
import { ITEMS, type ItemDef } from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  fetchMe,
  postPurchase,
  newIdempotencyKey,
  itemTextureKey,
  ensureItemTextures,
  type OutfitSlots,
  type SwimSlots,
} from '../../avatar/index.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
  type ObjectDef,
} from '../../interact/index.js';
import { WalkController } from '../../core/world/index.js';
import {
  RACK_TABS,
  RackView,
  applyEquip,
  clearSlot,
  hitDogForEquip,
  itemIdFromRackDef,
  type RackTabId,
} from './rackView.js';
import {
  loadSavedOutfits,
  saveOutfitToSlot,
  outfitFromSaved,
  clearOutfitSlot,
  type SavedOutfitSlot,
} from './savedOutfits.js';
import {
  createOutfitPersist,
  disposeOutfitPersist,
  type OutfitPersistController,
} from './outfitPersist.js';
import { equipJuicePlan } from './equipJuice.js';
import {
  INTERACT_BUS_EVENT,
  createWardrobeHatProgress,
  recordWardrobeHat,
  type WardrobeHatProgress,
} from './missionHook.js';

export class WardrobeScene extends BaseScene {
  static readonly KEY = 'Wardrobe';

  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private layer: ObjectLayer | null = null;
  private rack: RackView | null = null;
  private persist: OutfitPersistController | null = null;

  private slots: OutfitSlots = OutfitStore.load().slots;
  private swimSlots: SwimSlots = OutfitStore.load().swimSlots;
  private version = 0;
  private owned = new Set<string>();
  private balance = 0;
  private activeTab: RackTabId = 'body';
  private saved: SavedOutfitSlot[] = loadSavedOutfits();

  private statusText: Phaser.GameObjects.Text | null = null;
  private balanceText: Phaser.GameObjects.Text | null = null;
  private tabButtons: Phaser.GameObjects.Container[] = [];
  private mirrorSlots: Phaser.GameObjects.Container[] = [];
  private polaroids: Phaser.GameObjects.Container[] = [];
  private buyLayer: Phaser.GameObjects.Container | null = null;
  private pendingBuy: ItemDef | null = null;
  private buying = false;
  private unsubLayer: (() => void) | null = null;
  /** Dedup: jeden drop na psa emituje drag-end ORAZ snap — nie zakładaj dwa razy. */
  private lastDragDropId: string | null = null;
  private equipSequence = 0;
  /** W9-B: postęp misji wardrobe_try_hats (unikalne czapki). */
  private hatQuestProgress: WardrobeHatProgress = createWardrobeHatProgress();

  /** Pozycje salonu. */
  private readonly dogHome = { x: 380, y: 400 };
  private readonly mirrorX = 640;
  private readonly mirrorY = 300;
  private readonly rackOrigin = { x: 520, y: 560 };

  constructor() {
    super(WardrobeScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_wardrobe');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0xffe4f0);
    this.addRealBg('bg_wardrobe');
    this.paintSalon();

    // Cały katalog ubranek ma teksturę (PNG lub proceduralną) — wieszak + piesek
    try {
      ensureItemTextures(
        this,
        ITEMS.map((i) => i.id),
      );
    } catch {
      /* ignore */
    }

    const cache = OutfitStore.load();
    this.slots = cache.slots;
    this.swimSlots = cache.swimSlots;
    this.version = cache.version;
    this.owned = new Set(cache.ownedItems);
    this.balance = cache.balanceZl;
    this.saved = loadSavedOutfits();

    this.persist = createOutfitPersist({
      version: this.version,
      onStatus: (m) => this.setStatus(m),
      onVersion: (v) => {
        this.version = v;
      },
    });

    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: this.dogHome.x,
      y: this.dogHome.y,
      breed: cache.breed,
      color: cache.color,
      scale: 1.45,
      slots: this.slots,
      swimSlots: this.swimSlots,
      mode: 'normal',
      depth: 40,
    });

    this.walk = new WalkController(this, this.avatar, {
      bounds: {
        minX: 120,
        minY: 280,
        maxX: this.gameW - 120,
        maxY: 480,
      },
      speed: 160,
      enableInput: true,
    });

    this.buildChrome();
    this.buildMirrorSlots();
    this.buildPolaroids();
    this.rack = new RackView({
      scene: this,
      originX: this.rackOrigin.x,
      originY: this.rackOrigin.y,
      pageSize: 5,
      spacing: 108,
      depth: 55,
      onPageChange: () => this.rebuildRack(),
    });
    this.rebuildRack();

    this.events.once('shutdown', () => this.teardown());
    this.registry.set('companionFollow', {
      x: this.avatar.x,
      y: this.avatar.y,
    });
    void this.refreshFromServer();
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    // WalkController = autorytet pozycji; avatar.update = animacja (pozycję korygujemy).
    this.walk?.update(dt);
    if (this.walk && this.avatar) {
      const pos = this.walk.position;
      this.avatar.update(dt);
      this.avatar.container.setPosition(pos.x, pos.y);
      this.registry.set('companionFollow', { x: pos.x, y: pos.y });
    } else {
      this.avatar?.update(dt);
    }
    this.layer?.update(dt);
    // snap dog podąża za psem
    if (this.layer && this.avatar) {
      this.layer.setSnapZones([
        {
          id: 'dog',
          x: this.avatar.x,
          y: this.avatar.y - 10,
          radius: 96,
        },
      ]);
    }
  }

  // ── Salon décor ────────────────────────────────────────────────────

  private paintSalon(): void {
    // pastelowa podłoga / ściana gdy brak tła
    this.add
      .rectangle(this.gameW / 2, 520, this.gameW, 280, 0xf8e8f0, 0.35)
      .setDepth(-50);
    // wielkie lustro
    const mx = this.mirrorX;
    const my = this.mirrorY;
    this.add
      .rectangle(mx, my, 220, 280, 0xd8f0ff, 0.55)
      .setStrokeStyle(10, 0xffd700, 0.9)
      .setDepth(5);
    this.add
      .ellipse(mx, my - 20, 160, 200, 0xffffff, 0.18)
      .setDepth(6);
    this.add
      .text(mx, my - 150, '🪞 Lustro', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(7);
    // dywan
    this.add
      .ellipse(this.dogHome.x, this.dogHome.y + 50, 220, 48, 0xffb6c1, 0.35)
      .setDepth(1);
  }

  private buildChrome(): void {
    this.add
      .text(this.gameW / 2, 28, 'Salon Piękności', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(200);

    // saldo
    this.add.circle(70, 36, 22, 0xffd700).setStrokeStyle(2, 0xffffff).setDepth(200);
    this.balanceText = this.add
      .text(100, 36, `${this.balance} zł`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(200);

    this.statusText = this.add
      .text(this.gameW / 2, 62, 'Kliknij ubranko albo przeciągnij na pieska!', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setDepth(200);

    // pasek kategorii
    const startX = 70;
    const y = 110;
    this.tabButtons = [];
    RACK_TABS.forEach((tab, i) => {
      const x = startX + i * 76;
      const btn = this.makeTabButton(x, y, tab);
      this.tabButtons.push(btn);
    });
    this.highlightTabs();

    UiKit.button(this, {
      x: 160,
      y: 680,
      label: '🐾 przejdź się!',
      fill: 0xb8e6d0,
      width: 220,
      height: MIN_TOUCH,
      name: 'walk-show',
      onClick: () => this.walkShow(),
    }).setDepth(200);

    UiKit.button(this, {
      x: this.gameW / 2,
      y: 680,
      label: 'Sklep',
      fill: 0xe6e0f8,
      width: 140,
      height: MIN_TOUCH,
      name: 'open-shop',
      onClick: () => {
        void this.persist?.flush();
        getSceneRouter().go('Shop');
      },
    }).setDepth(200);

    UiKit.button(this, {
      x: this.gameW - 180,
      y: 680,
      label: 'Wróć do miasteczka',
      fill: 0xffb6c1,
      width: 240,
      height: MIN_TOUCH,
      name: 'back-to-town',
      onClick: () => {
        void this.persist?.flush();
        getSceneRouter().go('Town');
      },
    }).setDepth(200);
  }

  private makeTabButton(
    x: number,
    y: number,
    tab: (typeof RACK_TABS)[number],
  ): Phaser.GameObjects.Container {
    const size = MIN_TOUCH;
    const bg = this.add
      .rectangle(0, 0, size, size, tab.iconColor)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const label = this.add
      .text(0, 0, tab.labelPl.slice(0, 7), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#4a3f55',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: size - 6 },
      })
      .setOrigin(0.5);
    const c = this.add.container(x, y, [bg, label]);
    c.setSize(size, size);
    c.setDepth(200);
    c.setData('tabId', tab.id);
    bg.on('pointerdown', () => this.selectTab(tab.id));
    return c;
  }

  private selectTab(id: RackTabId): void {
    this.activeTab = id;
    if (id === 'swim') this.avatar?.setMode('swim');
    else this.avatar?.setMode('normal');
    this.highlightTabs();
    this.rebuildRack();
    this.refreshMirrorSlots();
  }

  private highlightTabs(): void {
    for (const c of this.tabButtons) {
      const id = c.getData('tabId') as RackTabId;
      const bg = c.list[0] as Phaser.GameObjects.Rectangle;
      const active = id === this.activeTab;
      bg.setStrokeStyle(active ? 5 : 3, active ? 0x4a3f55 : 0xffffff);
      c.setScale(active ? 1.08 : 1);
    }
  }

  // ── Lustro: sloty do zdejmowania ───────────────────────────────────

  private buildMirrorSlots(): void {
    this.mirrorSlots.forEach((c) => c.destroy(true));
    this.mirrorSlots = [];
    const labels: { id: RackTabId; label: string }[] = [
      { id: 'hat', label: 'Czapka' },
      { id: 'body', label: 'Ubranko' },
      { id: 'collar', label: 'Obroża' },
      { id: 'accessory', label: 'Dodatek' },
      { id: 'shoes', label: 'Buciki' },
      { id: 'bow', label: 'Kokardka' },
      { id: 'glasses', label: 'Okulary' },
      { id: 'swim', label: 'Kąpiel' },
    ];
    labels.forEach((slot, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = this.mirrorX - 70 + col * 140;
      const y = this.mirrorY + 120 + row * 36;
      // małe chipy przy lustrze — ale ≥64 wysokości skumulowanej; cel = 64 szerokości
      const w = 120;
      const h = 32;
      const bg = this.add
        .rectangle(0, 0, w, Math.max(h, 32), 0xffffff, 0.85)
        .setStrokeStyle(2, 0xffd700)
        .setInteractive({ useHandCursor: true });
      // hit area ≥64
      bg.setInteractive(
        new Phaser.Geom.Rectangle(-Math.max(w, MIN_TOUCH) / 2, -MIN_TOUCH / 2, Math.max(w, MIN_TOUCH), MIN_TOUCH),
        Phaser.Geom.Rectangle.Contains,
      );
      const t = this.add
        .text(0, 0, slot.label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#4a3f55',
        })
        .setOrigin(0.5);
      const c = this.add.container(x, y, [bg, t]);
      c.setDepth(30);
      c.setData('slotId', slot.id);
      bg.on('pointerdown', () => this.unequipSlot(slot.id));
      this.mirrorSlots.push(c);
    });
    this.refreshMirrorSlots();
  }

  private refreshMirrorSlots(): void {
    for (const c of this.mirrorSlots) {
      const id = c.getData('slotId') as RackTabId;
      const t = c.list[1] as Phaser.GameObjects.Text;
      const bg = c.list[0] as Phaser.GameObjects.Rectangle;
      let filled = false;
      if (id === 'swim') {
        filled = !!(this.swimSlots.suit || this.swimSlots.cap);
      } else {
        filled = !!this.slots[id];
      }
      bg.setFillStyle(filled ? 0xb8e6d0 : 0xffffff, 0.9);
      const base = this.mirrorLabel(id);
      t.setText(filled ? `✕ ${base}` : base);
    }
  }

  private mirrorLabel(id: RackTabId): string {
    const m: Record<string, string> = {
      hat: 'Czapka',
      body: 'Ubranko',
      collar: 'Obroża',
      accessory: 'Dodatek',
      shoes: 'Buciki',
      bow: 'Kokardka',
      glasses: 'Okulary',
      swim: 'Kąpiel',
    };
    return m[id] ?? id;
  }

  private unequipSlot(id: RackTabId): void {
    const next = clearSlot(id, this.slots, this.swimSlots);
    this.slots = next.slots;
    this.swimSlots = next.swimSlots;
    this.applyOutfitToAvatar();
    this.persist?.schedule(this.slots, this.swimSlots);
    this.setStatus('Zdejęte ✨');
    this.refreshMirrorSlots();
    this.rebuildRack();
    void this.avatar?.playAction('wave');
  }

  // ── Polaroidy ──────────────────────────────────────────────────────

  private buildPolaroids(): void {
    this.polaroids.forEach((c) => c.destroy(true));
    this.polaroids = [];
    const baseX = 70;
    const baseY = 220;
    for (let i = 0; i < 4; i++) {
      const y = baseY + i * 100;
      const slot = this.saved[i];
      const frame = slot?.frameColor ?? 0xf0e6ee;
      const bg = this.add
        .rectangle(0, 0, 100, 88, 0xfffaf5)
        .setStrokeStyle(4, frame)
        .setInteractive({ useHandCursor: true });
      // cel ≥64
      bg.setInteractive(
        new Phaser.Geom.Rectangle(-50, -44, 100, 88),
        Phaser.Geom.Rectangle.Contains,
      );
      const thumb = this.add
        .circle(0, -8, 22, slot ? 0xff9ec8 : 0xe0d8e8)
        .setStrokeStyle(2, 0xffffff);
      const label = this.add
        .text(0, 28, slot ? slot.namePl ?? `Zestaw ${i + 1}` : 'pusty', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#4a3f55',
          align: 'center',
          wordWrap: { width: 90 },
        })
        .setOrigin(0.5);
      const c = this.add.container(baseX, y, [bg, thumb, label]);
      c.setDepth(25);
      c.setSize(100, 88);
      const idx = i;
      bg.on('pointerdown', () => this.onPolaroid(idx));
      if (slot) {
        // badge „✕" w rogu ramki — kasuje zapisany zestaw (poza hit-areą bg, więc nie koliduje z zakładaniem)
        const delBg = this.add
          .circle(54, -46, 16, 0xffb6c1, 0.98)
          .setStrokeStyle(2, 0xffffff)
          .setInteractive({ useHandCursor: true });
        const delLabel = this.add
          .text(54, -46, '✕', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '16px',
            color: '#4a3f55',
            fontStyle: 'bold',
          })
          .setOrigin(0.5);
        delBg.on('pointerdown', () => this.clearPolaroid(idx));
        c.add([delBg, delLabel]);
      }
      this.polaroids.push(c);
    }
    // przycisk zapisu aktywnego stroju do pierwszego wolnego / ostatnio
    UiKit.button(this, {
      x: 70,
      y: 620,
      label: '📷 zapisz',
      fill: 0xffe4a0,
      width: 100,
      height: MIN_TOUCH,
      onClick: () => this.saveToNextPolaroid(),
    }).setDepth(200);
  }

  private onPolaroid(index: number): void {
    const slot = this.saved[index];
    if (!slot) {
      // pusty → zapisz bieżący
      this.saved = saveOutfitToSlot(index, this.slots, this.swimSlots, this.saved);
      this.buildPolaroids();
      this.setStatus('Zapisano zestaw! 📷');
      return;
    }
    const o = outfitFromSaved(slot);
    this.slots = o.slots;
    this.swimSlots = o.swimSlots;
    this.applyOutfitToAvatar();
    this.persist?.schedule(this.slots, this.swimSlots);
    this.setStatus(`Założono: ${slot.namePl ?? 'zestaw'} ✨`);
    this.refreshMirrorSlots();
    this.rebuildRack();
    void this.avatar?.playAction('happy');
  }

  private saveToNextPolaroid(): void {
    let idx = this.saved.findIndex((s) => s == null);
    if (idx < 0) idx = 0;
    this.saved = saveOutfitToSlot(idx, this.slots, this.swimSlots, this.saved);
    this.buildPolaroids();
    this.setStatus(`Zapisano w ramce ${idx + 1} 📷`);
  }

  private clearPolaroid(index: number): void {
    this.saved = clearOutfitSlot(index, this.saved);
    this.buildPolaroids();
    this.setStatus('Usunięto zestaw z ramki 🗑️');
  }

  // ── Karuzela wieszaków ─────────────────────────────────────────────

  private rebuildRack(): void {
    if (!this.rack || !this.avatar) return;

    this.unsubLayer?.();
    this.unsubLayer = null;
    this.layer?.destroy();
    this.layer = null;

    // upewnij się przed spawnem wieszaków (InteractiveObject wymaga texture key)
    try {
      ensureItemTextures(
        this,
        ITEMS.map((i) => i.id),
      );
    } catch {
      /* ignore */
    }

    const { views, layout, defs } = this.rack.compute(
      this.activeTab,
      this.owned,
      this.slots,
      this.swimSlots,
      ITEMS,
    );

    const target = this.makeInteractTarget();
    this.layer = new ObjectLayer({
      scene: this,
      defs: defs as ObjectDef[],
      target,
      interactRange: 110,
      groundBounds: {
        minX: 80,
        maxX: this.gameW - 80,
        minY: 360,
        maxY: 620,
      },
    });
    this.layer.setSnapZones([
      { id: 'dog', x: this.avatar.x, y: this.avatar.y - 10, radius: 96 },
    ]);
    this.layer.spawn(layout.placements);
    this.rack.paintOverlays(views, layout);

    this.unsubLayer = this.layer.on((evt) => this.onInteract(evt));
  }

  private makeInteractTarget(): InteractTarget {
    const av = this.avatar!;
    return {
      moveTo: (x, y) => av.moveTo(x, y),
      holdItem: (k) => av.holdItem(k),
      playAction: (a) => av.playAction(a as 'happy'),
      getPosition: () => ({ x: av.x, y: av.y }),
      get x() {
        return av.x;
      },
      get y() {
        return av.y;
      },
      get isMoving() {
        return av.isMoving;
      },
    };
  }

  private onInteract(evt: InteractEvent): void {
    const itemId = itemIdFromRackDef(evt.defId);
    if (!itemId) return;
    const item = ITEMS.find((i) => i.id === itemId);
    if (!item) return;

    if (evt.type === 'tap') {
      if (!this.owned.has(item.id)) {
        this.openBuy(item);
        return;
      }
      this.equipItem(item, 'tap');
      return;
    }

    if (evt.type === 'snap' && evt.snapZoneId === 'dog') {
      // drag-end tego samego dropu już obsłużył założenie/kupno — pomiń duplikat.
      if (this.lastDragDropId === evt.objectId) {
        this.lastDragDropId = null;
        this.returnHangerHome(evt.objectId);
        return;
      }
      if (!this.owned.has(item.id)) {
        this.openBuy(item);
        this.returnHangerHome(evt.objectId);
        return;
      }
      this.equipItem(item, 'drag');
      this.returnHangerHome(evt.objectId);
      return;
    }

    if (evt.type === 'drag-end') {
      // drag-end poprzedza ewentualny snap w tym samym geście — czyścimy znacznik.
      this.lastDragDropId = null;
      const dog = this.avatar;
      if (!dog) return;
      if (hitDogForEquip(evt.x, evt.y, dog.x, dog.y, 100)) {
        if (!this.owned.has(item.id)) {
          this.openBuy(item);
        } else {
          this.equipItem(item, 'drag');
        }
        // oznacz, aby nadchodzący snap tego dropu nie zadziałał ponownie.
        this.lastDragDropId = evt.objectId;
      }
      this.returnHangerHome(evt.objectId);
    }
  }

  private returnHangerHome(objectId: string): void {
    const obj = this.layer?.getObject(objectId);
    if (!obj || !this.rack?.layout) return;
    const place = this.rack.layout.placements.find((p) => p.defId === obj.def.id);
    if (place) {
      obj.setPosition(place.x, place.y);
      obj.container.setVisible(true);
      obj.container.setAlpha(1);
    }
  }

  private equipItem(item: ItemDef, via: 'tap' | 'drag'): void {
    const next = applyEquip(item, this.slots, this.swimSlots, 'force');
    this.slots = next.slots;
    this.swimSlots = next.swimSlots;

    this.playEquipJuice(item, via);
    this.persist?.schedule(this.slots, this.swimSlots);
    this.setStatus(`Pasuje! ${item.namePl} ✨`);
    this.refreshMirrorSlots();
    this.rebuildRack();

    // W9-B: krok misji wardrobe_try_hats — tylko unikalne czapki
    const hatStep = recordWardrobeHat(this.hatQuestProgress, item.id, item.slot);
    this.hatQuestProgress = hatStep.progress;
    if (hatStep.payload) {
      this.game.events.emit(INTERACT_BUS_EVENT, hatStep.payload);
      this.events.emit('quest-progress', hatStep.payload);
    }
  }

  private playEquipJuice(item: ItemDef, via: 'tap' | 'drag'): void {
    const avatar = this.avatar;
    if (!avatar) {
      this.applyOutfitToAvatar();
      return;
    }
    const sequence = ++this.equipSequence;
    const tex = itemTextureKey(item.id);
    const pointer = this.input.activePointer;
    const start =
      via === 'drag'
        ? { x: pointer.worldX, y: pointer.worldY }
        : { x: this.rackOrigin.x, y: this.rackOrigin.y - 24 };
    const fly = this.textures.exists(tex)
      ? this.add.image(start.x, start.y, tex).setDisplaySize(62, 62)
      : this.add
          .rectangle(start.x, start.y, 52, 52, 0xff9ec8)
          .setStrokeStyle(3, 0xffffff);
    fly.setDepth(500);

    const plan = equipJuicePlan(via, avatar.prefersReducedMotion);
    const runFrame = (index: number): void => {
      const frame = plan.keyframes[index];
      if (!frame) {
        fly.destroy();
        return;
      }
      const settle = () => {
        if (frame.applyOutfit && sequence === this.equipSequence) {
          this.applyOutfitToAvatar();
          playFx(this, 'sparkle', avatar.x, avatar.y - 40);
          playFx(this, 'stars', avatar.x, avatar.y - 20);
          void avatar.playAction('happy');
        }
        if (index === plan.keyframes.length - 1) {
          fly.destroy();
          return;
        }
        runFrame(index + 1);
      };
      if (frame.durationMs === 0) {
        fly.setPosition(
          avatar.x + frame.offsetX,
          avatar.y - 24 + frame.offsetY,
        );
        fly.setScale(frame.scale);
        fly.setAngle(frame.angle);
        settle();
        return;
      }
      this.tweens.add({
        targets: fly,
        x: avatar.x + frame.offsetX,
        y: avatar.y - 24 + frame.offsetY,
        scaleX: frame.scale,
        scaleY: frame.scale,
        angle: frame.angle,
        duration: frame.durationMs,
        ease: 'Sine.easeInOut',
        onComplete: settle,
      });
    };
    runFrame(0);
  }

  private applyOutfitToAvatar(): void {
    this.avatar?.setOutfit(this.slots, this.swimSlots);
    if (this.activeTab === 'swim') this.avatar?.setMode('swim');
    else this.avatar?.setMode('normal');
  }

  // ── Zakup ──────────────────────────────────────────────────────────

  private openBuy(item: ItemDef): void {
    this.closeBuy();
    this.pendingBuy = item;
    const dim = this.add
      .rectangle(this.gameW / 2, this.gameH / 2, this.gameW, this.gameH, 0x4a3f55, 0.4)
      .setInteractive()
      .setDepth(300);
    const panel = this.add
      .rectangle(0, 0, 420, 260, 0xfff8f0)
      .setStrokeStyle(4, 0xffb6c1);
    const name = this.add
      .text(0, -70, item.namePl, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#4a3f55',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: 380 },
      })
      .setOrigin(0.5);
    const price = this.add
      .text(0, -20, `${item.priceZl} zł`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '30px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const root = this.add.container(this.gameW / 2, this.gameH / 2 - 20, [
      panel,
      name,
      price,
    ]);
    root.setDepth(301);

    const yes = UiKit.button(this, {
      x: this.gameW / 2 - 100,
      y: this.gameH / 2 + 70,
      label: `Kup za ${item.priceZl} zł`,
      fill: 0xb8e6d0,
      width: 200,
      height: MIN_TOUCH + 4,
      onClick: () => {
        this.closeBuy();
        void this.buy(item);
      },
    });
    yes.setDepth(302);

    const no = UiKit.button(this, {
      x: this.gameW / 2 + 120,
      y: this.gameH / 2 + 70,
      label: 'Nie',
      fill: 0xffb6c1,
      width: 120,
      height: MIN_TOUCH + 4,
      onClick: () => this.closeBuy(),
    });
    no.setDepth(302);

    this.buyLayer = this.add.container(0, 0, [dim, root, yes, no]);
    this.buyLayer.setDepth(300);
  }

  private closeBuy(): void {
    this.buyLayer?.destroy(true);
    this.buyLayer = null;
    this.pendingBuy = null;
  }

  private async buy(item: ItemDef): Promise<void> {
    if (this.buying) return;
    this.buying = true;
    this.setStatus('Kupuję…');
    const res = await postPurchase({
      kind: 'item',
      id: item.id,
      qty: 1,
      idempotencyKey: newIdempotencyKey(),
    });
    this.buying = false;

    if (res.ok) {
      this.balance = res.data.balance;
      if (res.data.ownedItems) {
        this.owned = new Set(res.data.ownedItems);
      } else {
        this.owned.add(item.id);
      }
      OutfitStore.commitPurchase(
        this.balance,
        res.data.ownedItems ?? [...this.owned],
      );
      this.balanceText?.setText(`${this.balance} zł`);
      // od razu założone
      this.equipItem(item, 'tap');
      this.setStatus('Kupione i założone! 🎉');
      return;
    }
    if (res.code === 'INSUFFICIENT_FUNDS') {
      this.setStatus('Za mało złotówek');
      return;
    }
    if (res.code === 'UNAUTHORIZED') {
      this.setStatus('Zaloguj się ponownie, aby kupować.');
      return;
    }
    if (res.status === 0) {
      this.setStatus('Potrzebna sieć do zakupów');
      return;
    }
    this.setStatus('Spróbuj później');
  }

  // ── Spacer ─────────────────────────────────────────────────────────

  private walkShow(): void {
    if (!this.avatar || !this.walk) return;
    const fromLeft = this.avatar.x < this.gameW / 2;
    const targetX = fromLeft ? this.gameW - 180 : 180;
    const targetY = 400 + (Math.random() - 0.5) * 40;
    this.walk.moveTo(targetX, targetY);
    this.setStatus('Patrz — strój w ruchu! 🐾');
  }

  // ── Server ─────────────────────────────────────────────────────────

  private async refreshFromServer(): Promise<void> {
    const me = await fetchMe();
    if (!me.ok) return;
    this.version = me.data.profileVersion;
    this.persist?.setVersion(this.version);
    OutfitStore.saveVersion(this.version);
    OutfitStore.saveDog({
      breed: me.data.breed,
      color: me.data.color,
      dogName: me.data.dogName,
    });
    this.balance = me.data.balanceZl;
    OutfitStore.saveBalance(this.balance);
    this.balanceText?.setText(`${this.balance} zł`);
    this.avatar?.setBreed(me.data.breed, me.data.color);
    // strój z lokalnego lustra (serwer outfit nie w /me — PUT jest SoT po zapisie)
    const cache = OutfitStore.load();
    this.slots = cache.slots;
    this.swimSlots = cache.swimSlots;
    this.applyOutfitToAvatar();
  }

  private setStatus(msg: string): void {
    this.statusText?.setText(msg);
  }

  private teardown(): void {
    // G7 / race: await flush (dirty follow-up PUT) przed destroy
    disposeOutfitPersist(this.persist);
    this.persist = null;
    this.unsubLayer?.();
    this.unsubLayer = null;
    this.layer?.destroy();
    this.layer = null;
    this.rack?.destroy();
    this.rack = null;
    this.walk?.destroy();
    this.walk = null;
    this.avatar?.destroy();
    this.avatar = null;
    this.closeBuy();
    this.registry.remove('companionFollow');
  }
}

export default WardrobeScene;
